import { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { AuthService } from "@/hooks/services/authService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { useAvailabilityCheck } from "@/hooks/useAvailabilityCheck";
import {
  Mail, Lock, User, School, ArrowRight,
  CheckCircle2, AlertCircle, Eye, EyeOff, GraduationCap,
  Building2, Loader2, CircleCheck, CircleX,
} from "lucide-react";

// ── Shared helpers ─────────────────────────────────────────────────────────────

function AvailBadge({ status, labels = {} }) {
  const l = {
    checking:  "checking…",
    available: "available",
    taken:     "already taken",
    error:     "could not verify",
    ...labels,
  };
  if (status === "idle") return null;
  if (status === "checking") return (
    <span className="flex items-center gap-1 text-xs text-slate-400 font-medium">
      <Loader2 className="w-3 h-3 animate-spin" /> {l.checking}
    </span>
  );
  if (status === "available") return (
    <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
      <CircleCheck className="w-3.5 h-3.5" /> {l.available}
    </span>
  );
  if (status === "taken") return (
    <span className="flex items-center gap-1 text-xs text-red-500 font-semibold">
      <CircleX className="w-3.5 h-3.5" /> {l.taken}
    </span>
  );
  return <span className="text-xs text-amber-500 font-medium">{l.error}</span>;
}

function fieldBorder(status) {
  if (status === "available") return "border-emerald-400 ring-2 ring-emerald-500/20";
  if (status === "taken")     return "border-red-400   ring-2 ring-red-400/20";
  return "border-slate-200/80";
}

// ── Page ───────────────────────────────────────────────────────────────────────

const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "",
    password: "", confirmPassword: "", schoolCode: "",
  });
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState("");

  // Live availability / existence checks
  const emailCheck = useAvailabilityCheck({ type: "email",              minLength: 6,  debounceMs: 800 });
  const codeCheck  = useAvailabilityCheck({ type: "school-code-lookup", minLength: 3,  debounceMs: 800 });

  // School info returned by the code lookup (name + branches)
  const schoolInfo = codeCheck.status === "available" ? codeCheck.data : null;
  const branches   = (schoolInfo?.branches ?? []).filter(b => b.isActive !== false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
    if (name === "email")      emailCheck.check(value);
    if (name === "schoolCode") {
      codeCheck.check(value);
      setSelectedBranchId(""); // reset branch when code changes
    }
  }, [error, emailCheck, codeCheck]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError("Please fill in all fields"); return;
    }
    if (!formData.schoolCode) {
      setError("Please enter your school code"); return;
    }
    if (codeCheck.status === "taken" || codeCheck.status === "error") {
      setError("Please enter a valid school code"); return;
    }
    if (branches.length > 0 && !selectedBranchId) {
      setError("Please select your campus / branch"); return;
    }
    if (emailCheck.status === "taken") {
      setError("That email is already registered"); return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match"); return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters"); return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Terms and Conditions"); return;
    }

    setIsLoading(true);
    try {
      const result = await AuthService.registerStudent({
        name:       `${formData.firstName} ${formData.lastName}`,
        email:      formData.email,
        password:   formData.password,
        schoolCode: formData.schoolCode.toUpperCase(),
        branchId:   selectedBranchId || undefined,
      });

      AuthStore.getState().login(true, "student", result.user, result.accessToken, result.refreshToken);
      setSuccess(true);
      setTimeout(() => navigate("/onboarding"), 1500);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message || "Failed to create account.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputBase = "w-full pl-11 pr-4 py-3.5 bg-white/70 border rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50";
  const pwBase    = "w-full pl-11 pr-11 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50";

  const blocked = isLoading || success || emailCheck.status === "taken" || codeCheck.status === "taken";

  return (
    <main className="min-h-screen flex bg-linear-to-br from-slate-50 via-blue-50 to-slate-100">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full flex flex-col lg:flex-row relative z-10">
        {/* Image panel */}
        <div className="hidden lg:flex w-1/2 items-center justify-center p-10">
          <div className="relative w-full max-w-md">
            <img src="/students.avif" alt="Students"
              className="w-full h-auto rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-300" />
            <div className="absolute -bottom-4 -right-4 bg-blue-600 bg-linear-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full font-bold text-base shadow-lg">
              Student Journey
            </div>
          </div>
        </div>

        {/* Form panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 overflow-y-auto">
          <div className="w-full max-w-xl space-y-7 py-10">
            <button onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors">
              <GraduationCap className="w-4 h-4" />
              Student Portal
              <span className="text-slate-300">·</span>
              <span className="text-blue-600 hover:underline">Change portal</span>
            </button>

            <div className="space-y-1">
              <h1 className="text-3xl font-bold text-slate-900">Create your account</h1>
              <p className="text-slate-500 text-sm">Join EarlyScape and start your learning journey</p>
            </div>

            <div className="backdrop-blur-xl bg-white/50 border border-white/70 rounded-3xl p-8 sm:p-10 shadow-2xl">
              {success && (
                <div className="mb-6 p-4 bg-green-50/80 border border-green-200/50 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  <span className="text-green-800 font-medium text-sm">Account created! Redirecting…</span>
                </div>
              )}
              {error && !success && (
                <div className="mb-6 p-4 bg-red-50/80 border border-red-200/50 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-red-800 font-medium text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">

                {/* ── Name ── */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">First Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" name="firstName" placeholder="John"
                        value={formData.firstName} onChange={handleChange} disabled={isLoading || success}
                        className={`${inputBase} border-slate-200/80`} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Last Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" name="lastName" placeholder="Doe"
                        value={formData.lastName} onChange={handleChange} disabled={isLoading || success}
                        className={`${inputBase} border-slate-200/80`} />
                    </div>
                  </div>
                </div>

                {/* ── School code ── */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">School Code</label>
                    <AvailBadge
                      status={codeCheck.status}
                      labels={{ available: schoolInfo?.name ?? "valid school", taken: "school not found" }}
                    />
                  </div>
                  <div className="relative">
                    <School className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" name="schoolCode" placeholder="e.g. GRFLD"
                      value={formData.schoolCode} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} ${fieldBorder(codeCheck.status)}`} />
                  </div>
                  <p className="text-xs text-slate-400">Your school admin will share this code with you.</p>
                </div>

                {/* ── Branch selector — appears when school has branches ── */}
                {branches.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">
                      Campus / Branch <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <select
                        value={selectedBranchId}
                        onChange={(e) => setSelectedBranchId(e.target.value)}
                        disabled={isLoading || success}
                        className={`${inputBase} appearance-none border-slate-200/80`}
                      >
                        <option value="">Select your campus…</option>
                        {branches.map(b => (
                          <option key={b.id} value={b.id}>
                            {b.name}{b.city ? ` — ${b.city}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* ── Email ── */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">Email Address</label>
                    <AvailBadge status={emailCheck.status} />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="email" name="email" placeholder="your@email.com"
                      value={formData.email} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} ${fieldBorder(emailCheck.status)}`} />
                  </div>
                </div>

                {/* ── Password ── */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type={showPassword ? "text" : "password"} name="password" placeholder="Min. 8 chars"
                        value={formData.password} onChange={handleChange} disabled={isLoading || success}
                        className={pwBase} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type={showConfirm ? "text" : "password"} name="confirmPassword" placeholder="Repeat password"
                        value={formData.confirmPassword} onChange={handleChange} disabled={isLoading || success}
                        className={pwBase} />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Terms ── */}
                <div className="flex items-center gap-3 py-1">
                  <input type="checkbox" id="terms" checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={isLoading || success}
                    className="w-4 h-4 rounded border-slate-300 bg-white cursor-pointer accent-blue-700" />
                  <label htmlFor="terms" className="text-sm text-slate-600 select-none">
                    I agree to the{" "}
                    <a href="#" className="text-blue-600 hover:text-blue-700 font-semibold">Terms and Conditions</a>
                  </label>
                </div>

                <button type="submit" disabled={blocked}
                  className="w-full py-3.5 px-4 rounded-xl bg-blue-600 bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold text-sm shadow-lg transition-all disabled:opacity-50">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating Account…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Create Account <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200/60" />
                <span className="text-xs text-slate-400 font-medium">Already have an account?</span>
                <div className="flex-1 h-px bg-slate-200/60" />
              </div>

              <button type="button" onClick={() => navigate("/signin")}
                className="w-full py-3.5 px-4 bg-white/50 hover:bg-white/80 border border-slate-200/70 rounded-xl text-slate-700 font-semibold text-sm transition-all text-center">
                Sign In Instead
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignupPage;
